export class ContextMenu {
    constructor() {
        this.isVisible = false;
        this.currentView = null; // 'overview', 'zoomed', 'detail'
        this.currentImageData = null;
        this.callbacks = {};
        this.layoutInfo = { rows: 6, columns: 6 }; // Default to 6x6
        this.navigationController = null; // Reference to NavigationController
        
        // Long press state for mobile
        this.longPressTimer = null;
        this.longPressThreshold = 500; // 500ms
        this.longPressStartPos = { x: 0, y: 0 };
        this.longPressMoveThreshold = 10; // pixels
        
        this.createContextMenu();
        this.setupEventListeners();
    }
    
    createContextMenu() {
        this.container = document.createElement('div');
        this.container.className = 'context-menu';
        this.container.style.cssText = `
            position: fixed;
            background: rgba(0, 0, 0, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 8px;
            padding: 8px 0;
            min-width: 200px;
            z-index: 2000;
            opacity: 0;
            transform: scale(0.9);
            pointer-events: none;
            transition: opacity 0.2s ease, transform 0.2s ease;
            font-family: "IBM Plex Mono", "SF Mono", Menlo, Monaco, Consolas, monospace;
            font-size: 14px;
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
        `;
        
        document.body.appendChild(this.container);
    }
    
    setupEventListeners() {
        // Store bound references for cleanup in dispose()
        this._listeners = [];
        const addListener = (target, event, handler, options) => {
            target.addEventListener(event, handler, options);
            this._listeners.push({ target, event, handler, options });
        };

        // Desktop right-click prevention and custom handling
        addListener(document, 'contextmenu', (e) => {
            e.preventDefault();
            this.handleContextMenu(e);
        });

        // Mobile long-press handling
        addListener(document, 'touchstart', (e) => {
            this.handleTouchStart(e);
        }, { passive: false });

        addListener(document, 'touchmove', (e) => {
            this.handleTouchMove(e);
        }, { passive: false });

        addListener(document, 'touchend', (e) => {
            this.handleTouchEnd(e);
        }, { passive: false });

        // Hide context menu on any interaction outside
        addListener(document, 'click', (e) => {
            // Ignore Control+Click (macOS right-click simulation)
            if (e.ctrlKey) return;

            if (this.isVisible && !this.container.contains(e.target)) {
                this.hide();
            }
        });

        // ContextMenu keyboard handling moved to NavigationController

        // Hide on scroll, resize, or any mouse movement after a delay
        addListener(window, 'scroll', () => {
            if (this.isVisible) this.hide();
        });
        addListener(window, 'resize', () => {
            if (this.isVisible) this.hide();
        });
        addListener(window, 'wheel', () => {
            if (this.isVisible) this.hide();
        });

        // Hide on any gesture or pan events (for Three.js canvas interactions)
        addListener(document, 'pointerdown', (e) => {
            if (this.isVisible && !this.container.contains(e.target)) {
                this.hide();
            }
        });
    }
    
    handleContextMenu(e) {
        const viewContext = this.determineViewContext(e.target, { x: e.clientX, y: e.clientY });
        if (viewContext) {
            this.show(e.clientX, e.clientY, viewContext);
        }
    }
    
    handleTouchStart(e) {
        if (e.touches.length !== 1) return;
        
        const touch = e.touches[0];
        this.longPressStartPos = { x: touch.clientX, y: touch.clientY };
        
        this.longPressTimer = setTimeout(() => {
            const viewContext = this.determineViewContext(e.target, { x: touch.clientX, y: touch.clientY });
            if (viewContext) {
                // Prevent default image save behavior on detail view
                if (viewContext.view === 'detail') {
                    e.preventDefault();
                }
                this.show(touch.clientX, touch.clientY, viewContext);
                // Add haptic feedback if available
                if (navigator.vibrate) {
                    navigator.vibrate(50);
                }
            }
        }, this.longPressThreshold);
    }
    
    handleTouchMove(e) {
        if (!this.longPressTimer) return;
        
        const touch = e.touches[0];
        const deltaX = Math.abs(touch.clientX - this.longPressStartPos.x);
        const deltaY = Math.abs(touch.clientY - this.longPressStartPos.y);
        
        if (deltaX > this.longPressMoveThreshold || deltaY > this.longPressMoveThreshold) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    }
    
    handleTouchEnd(e) {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    }
    
    determineViewContext(target, position = null) {
        // Check if we're in detail view
        const detailView = document.querySelector('.detail-view-container');
        if (detailView && detailView.style.opacity !== '0' && detailView.contains(target)) {
            // Prevent default image save behavior on detail view images
            if (target.tagName === 'IMG') {
                target.style.webkitTouchCallout = 'none';
                target.style.webkitUserSelect = 'none';
                target.style.userSelect = 'none';
                target.draggable = false;
            }
            return {
                view: 'detail',
                imageData: this.currentImageData,
                position: position
            };
        }
        
        // Check if we're on the canvas (contact sheet)
        const canvas = document.querySelector('canvas');
        if (canvas && target === canvas) {
            // Determine if we're in overview or zoomed state
            // This will be set by the ContactSheet when state changes
            return {
                view: this.currentView || 'overview',
                imageData: this.currentImageData,
                position: position
            };
        }
        
        return null;
    }
    
    show(x, y, context) {
        this.currentView = context.view;
        this.currentImageData = context.imageData;
        
        this.container.innerHTML = '';
        this.createMenuItems(context);
        
        // Position the menu
        this.positionMenu(x, y);
        
        // Show the menu
        this.container.style.opacity = '1';
        this.container.style.transform = 'scale(1)';
        this.container.style.pointerEvents = 'auto';
        this.isVisible = true;
    }
    
    createMenuItems(context) {
        const items = this.getMenuItemsForView(context.view, context);
        
        items.forEach((item, index) => {
            const menuItem = document.createElement('div');
            menuItem.className = 'context-menu-item';
            
            const isDisabled = item.disabled || false;
            const baseStyles = `
                padding: 12px 16px;
                transition: background-color 0.15s ease;
                border-bottom: ${index < items.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'};
            `;
            
            if (isDisabled) {
                menuItem.style.cssText = baseStyles + `
                    color: rgba(255, 255, 255, 0.4);
                    cursor: default;
                `;
            } else {
                menuItem.style.cssText = baseStyles + `
                    color: white;
                    cursor: pointer;
                `;
                
                menuItem.addEventListener('mouseenter', () => {
                    menuItem.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                });
                
                menuItem.addEventListener('mouseleave', () => {
                    menuItem.style.backgroundColor = 'transparent';
                });
                
                menuItem.addEventListener('click', () => {
                    this.handleMenuItemClick(item.action, context);
                    this.hide();
                });
            }
            
            menuItem.textContent = item.label;
            this.container.appendChild(menuItem);
        });
    }
    
    getMenuItemsForView(view, context) {
        const imageData = context.imageData || {};
        
        // Calculate boundaries dynamically using layout info
        const maxRow = this.layoutInfo.rows - 1;
        const maxCol = this.layoutInfo.columns - 1;
        
        const isFirstImage = imageData.row === 0 && imageData.col === 0;
        const isLastImage = imageData.row === maxRow && imageData.col === maxCol;
        
        switch (view) {
            case 'overview':
                return [
                    { label: 'Zoom to Image', action: 'zoom_to_image' },
                    { label: 'View Image Details', action: 'open_detail' }
                ];
            
            case 'zoomed':
                return [
                    { label: 'Previous Image', action: 'previous_image', disabled: isFirstImage },
                    { label: 'Next Image', action: 'next_image', disabled: isLastImage },
                    { label: 'View Image Details', action: 'open_detail' },
                    { label: 'View All Images', action: 'zoom_out' }
                ];
            
            case 'detail':
                return [
                    { label: 'Previous Image', action: 'previous_image', disabled: isFirstImage },
                    { label: 'Next Image', action: 'next_image', disabled: isLastImage },
                    { label: 'Close Image Details', action: 'close_detail' },
                    { label: 'View All Images', action: 'back_to_contact' }
                ];
            
            default:
                return [];
        }
    }
    
    handleMenuItemClick(action, context) {
        // Route navigation through NavigationController if available
        if (this.navigationController) {
            const actionMap = {
                zoom_to_image: 'zoom-to-image',
                open_detail: 'open-detail',
                previous_image: 'previous-image',
                next_image: 'next-image',
                zoom_out: 'zoom-out',
                close_detail: 'close-detail',
                back_to_contact: 'back-to-contact'
            };
            
            const navigationAction = actionMap[action];
            if (navigationAction) {
                if (action === 'zoom_to_image') {
                    console.log(`ContextMenu: 'zoom_to_image' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position, source: 'context menu' });
                } else if (action === 'zoom_out') {
                    console.log(`ContextMenu: 'zoom_out' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                } else if (action === 'previous_image') {
                    console.log(`ContextMenu: 'previous_image' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                } else if (action === 'next_image') {
                    console.log(`ContextMenu: 'next_image' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                } else if (action === 'open_detail') {
                    console.log(`ContextMenu: 'open_detail' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                } else if (action === 'close_detail') {
                    console.log(`ContextMenu: 'close_detail' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                } else if (action === 'back_to_contact') {
                    console.log(`ContextMenu: 'back_to_contact' action. Routing to NC.`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                } else {
                    console.log(`ContextMenu: routing ${action} to NavigationController as ${navigationAction}`);
                    this.navigationController.navigate(navigationAction, { position: context.position });
                }
                return;
            }
        }
        
        // Fallback to old callback system if no NavigationController or unknown action
        if (this.callbacks[action]) {
            this.callbacks[action](context);
        }
    }
    
    positionMenu(x, y) {
        // First set position to measure the menu size
        this.container.style.left = `${x}px`;
        this.container.style.top = `${y}px`;
        
        // Force a layout to get accurate measurements
        this.container.offsetHeight;
        
        const rect = this.container.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Adjust position to keep menu within viewport
        let adjustedX = x;
        let adjustedY = y;
        
        // Check right edge
        if (x + rect.width > viewportWidth) {
            adjustedX = Math.max(10, viewportWidth - rect.width - 10);
        }
        
        // Check bottom edge
        if (y + rect.height > viewportHeight) {
            adjustedY = Math.max(10, viewportHeight - rect.height - 10);
        }
        
        // Ensure minimum margins from edges
        adjustedX = Math.max(10, adjustedX);
        adjustedY = Math.max(10, adjustedY);
        
        this.container.style.left = `${adjustedX}px`;
        this.container.style.top = `${adjustedY}px`;
    }
    
    hide() {
        if (!this.isVisible) return;
        
        this.container.style.opacity = '0';
        this.container.style.transform = 'scale(0.9)';
        this.container.style.pointerEvents = 'none';
        this.isVisible = false;
    }
    
    setView(view) {
        this.currentView = view;
    }
    
    setImageData(imageData) {
        this.currentImageData = imageData;
    }
    
    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }
    
    setLayoutInfo(layoutInfo) {
        this.layoutInfo = layoutInfo;
    }
    
    setNavigationController(navigationController) {
        this.navigationController = navigationController;
    }
    
    dispose() {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
        }

        // Remove all document/window-level listeners
        if (this._listeners) {
            for (const { target, event, handler, options } of this._listeners) {
                target.removeEventListener(event, handler, options);
            }
            this._listeners = [];
        }

        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
} 